import { PlantDto } from '@trucktrax/trucktrax-ts-common';
import { PlantFilterOption } from '../store/reducers/mapFilterReducers';
import { GeotraxUserPreferencesDto } from '../types';

// DEV-4973: the Incoming Materials Destination Plants list spans every product line in the
// region, so its selection is saved region-keyed under `incomingMaterials` rather than in
// the [region][productLine]-keyed `mapFilters` (see DEV-3820). It stores the *unchecked* ids,
// so a plant created after the save, or newly in scope, comes up checked by default (PO
// decision 2026-10-09). This deliberately differs from the main map's `selectedPlantIds`.
// No entry for a region means every plant is checked.

/** The saved unchecked ids for a region; [] when absent or malformed (the server stores free-form JSON). */
export function readIncomingDeselectedPlantIds(
  prefs: GeotraxUserPreferencesDto | undefined,
  regionId: string,
): number[] {
  const saved = prefs?.incomingMaterials?.[regionId]?.deselectedPlantIds;
  return Array.isArray(saved) ? saved : [];
}

/** A loaded plant, or an existing filter entry (its isSelected is ignored and recomputed). */
type IncomingPlant = Pick<PlantDto, 'id' | 'name' | 'url'>;

/** The Destination Plants filter entries for the loaded plants: checked unless saved as unchecked. */
export function buildIncomingPlantFilters(
  plants: IncomingPlant[],
  deselectedPlantIds: number[],
): PlantFilterOption[] {
  const deselected = new Set(deselectedPlantIds);
  return plants.map(plant => ({
    id: plant.id,
    name: plant.name,
    url: plant.url,
    isSelected: !deselected.has(plant.id),
  }));
}

/**
 * Merges the loaded list's selection into the saved one: saved ids for plants that are not
 * loaded (archived, product line removed, out of scope) keep their unchecked state, and the
 * loaded plants contribute exactly their current unchecked ids.
 *
 * An empty result removes the region's entry (absent means all checked), which also drops a
 * stale-only entry. Otherwise the entry is written as exactly { deselectedPlantIds }, so
 * unknown keys left in it (e.g. the previous build's selectedPlantIds) are not carried forward.
 *
 * `trustDisplay: false` is for a list hydrated from a failed preferences read: a loaded plant
 * shown checked may really be saved as unchecked, so saved ids are all kept and the loaded
 * plants only add their unchecked ids (next = saved ∪ loadedDeselected).
 *
 * Never mutates `prefs`: getUserPreferences can hand back its shared module-level default.
 */
export function mergeIncomingDeselectedPlantIds(
  prefs: GeotraxUserPreferencesDto,
  regionId: string,
  loadedPlantIds: number[],
  loadedDeselectedPlantIds: number[],
  { trustDisplay = true }: { trustDisplay?: boolean } = {},
): GeotraxUserPreferencesDto {
  const loaded = new Set(loadedPlantIds);
  const saved = readIncomingDeselectedPlantIds(prefs, regionId);
  const keptSaved = trustDisplay ? saved.filter(id => !loaded.has(id)) : saved;
  const deselectedPlantIds = Array.from(new Set([...keptSaved, ...loadedDeselectedPlantIds]));
  if (deselectedPlantIds.length === 0) {
    return prefs.incomingMaterials?.[regionId] === undefined
      ? prefs
      : clearIncomingPlantSelection(prefs, regionId);
  }
  return {
    ...prefs,
    incomingMaterials: {
      ...prefs.incomingMaterials,
      [regionId]: { deselectedPlantIds },
    },
  };
}

/**
 * Reset to defaults: removes the region's entry so the read returns [] and the list falls
 * back to all checked — the IM counterpart of the main map's `mapFilters: {}`. Other
 * regions are untouched. Never mutates `prefs`.
 */
export function clearIncomingPlantSelection(
  prefs: GeotraxUserPreferencesDto,
  regionId: string,
): GeotraxUserPreferencesDto {
  const incomingMaterials = { ...prefs.incomingMaterials };
  delete incomingMaterials[regionId];
  return { ...prefs, incomingMaterials };
}
